// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef } from 'react';
import { Pressable, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import FontAwesome from '@expo/vector-icons/FontAwesome';

import { useSpeechInput } from '@/hooks/useSpeechInput';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

type MicButtonProps = {
  /** The field's current text. Captured when recording starts, so dictation appends to it. */
  value: string;
  onChangeText: (text: string) => void;
  disabled?: boolean;
  /** Receives a ready-to-show message, or null when the error clears. */
  onError?: (message: string | null) => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

/**
 * Dictates into a text field.
 *
 * Renders nothing where speech recognition is unavailable — native, and browsers without the Web
 * Speech API — rather than showing a button that does nothing.
 *
 * Dictation **appends to** whatever is already in the field rather than replacing it. Replacing is
 * the obvious implementation and it silently eats text somebody typed before reaching for the mic.
 */
export default function MicButton({
  value,
  onChangeText,
  disabled = false,
  onError,
  style,
  testID,
}: MicButtonProps) {
  const { t, locale } = useI18n();

  // The text as it was when recording started. Interim results are revised in place, so each
  // update rewrites base + latest utterance rather than accumulating.
  const baseRef = useRef('');
  const valueRef = useRef(value);
  valueRef.current = value;

  const speech = useSpeechInput({
    locale,
    onTranscript: (transcript) => onChangeText(appendTranscript(baseRef.current, transcript)),
  });

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!onErrorRef.current) return;
    if (speech.errorCode === 'micDenied') {
      onErrorRef.current(t('speech.errors.denied'));
      return;
    }
    if (speech.errorCode === 'micFailed') {
      onErrorRef.current(t('speech.errors.failed'));
      return;
    }
    onErrorRef.current(null);
  }, [speech.errorCode, t]);

  if (!speech.supported) return null;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={speech.listening ? t('speech.stop') : t('speech.start')}
      style={[styles.button, speech.listening && styles.listening, disabled && styles.disabled, style]}
      disabled={disabled}
      onPress={() => {
        // Captured before toggling, so the first transcript already knows what to append to.
        if (!speech.listening) baseRef.current = valueRef.current;
        speech.toggle();
      }}
      testID={testID}
    >
      <FontAwesome
        name="microphone"
        size={18}
        color={speech.listening ? appColors.text.inverse : appColors.accent.primary}
      />
    </Pressable>
  );
}

/**
 * Joins dictation onto text that was already in the field, with exactly one space between.
 * Exported for its tests — the empty-base and trailing-space cases are where this goes wrong.
 */
export function appendTranscript(base: string, transcript: string): string {
  const trimmedBase = base.replace(/\s+$/, '');
  if (!trimmedBase) return transcript;
  if (!transcript) return trimmedBase;
  return `${trimmedBase} ${transcript}`;
}

const styles = StyleSheet.create({
  button: {
    width: 50,
    height: 50,
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.surface.input,
  },
  listening: {
    backgroundColor: appColors.accent.primary,
  },
  disabled: {
    opacity: 0.5,
  },
});
